import { useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

export interface UseTextareaAutosizeOptions {
  minRows?: number;
  maxRows?: number;
}

const DEFAULT_MIN_ROWS = 1;
const DEFAULT_MAX_ROWS = 6;
const FALLBACK_LINE_HEIGHT_PX = 20;

// Returns a CSS height string the consumer applies to the textarea's style.
// We avoid writing to the element's inline style directly so other consumer
// style props can't be clobbered by — or clobber — this hook's writes.
export function useTextareaAutosize(
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  value: string,
  options: UseTextareaAutosizeOptions = {},
): string | undefined {
  const { minRows = DEFAULT_MIN_ROWS, maxRows = DEFAULT_MAX_ROWS } = options;
  const [height, setHeight] = useState<string | undefined>(undefined);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    // Temporarily collapse so scrollHeight reflects content, not the previous size.
    const previousHeight = el.style.height;
    el.style.height = "auto";
    const computed = window.getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight) || FALLBACK_LINE_HEIGHT_PX;
    const padding = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    // scrollHeight never includes borders. With box-sizing: border-box the element's
    // CSS height also includes borders, so we have to add them back or the textarea
    // ends up a few pixels short and shows a scrollbar / clips its content.
    const borders =
      computed.boxSizing === "border-box"
        ? parseFloat(computed.borderTopWidth) + parseFloat(computed.borderBottomWidth)
        : 0;
    const contentMin = lineHeight * minRows + padding;
    const contentMax = lineHeight * maxRows + padding;
    const desired = Math.min(Math.max(el.scrollHeight, contentMin), contentMax);
    el.style.height = previousHeight;
    setHeight(`${desired + borders}px`);
  }, [textareaRef, value, minRows, maxRows]);

  return height;
}
